import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { compressImage } from "@/lib/compressImage";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { MasterSelect } from "@/components/MasterSelect";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Copy, UploadCloud, X, FileDown, FileSpreadsheet } from "lucide-react";
import { FabricImage } from "@/components/FabricImage";

const PRODUCT_TYPE_LABEL = { shade_wise: "Shade-wise", without_shade: "Without shade", pcs: "PCS" };

const empty = {
  name: "",
  product_type: "shade_wise",
  print_name: "",
  company: "",
  quality: "",
  main_unit: "",
  alternative_unit: "",
  hsn_code: "",
  sales_price: "",
  purchase_price: "",
  min_stock: "",
  gst_pct: "",
  image_path: null,
};

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

export default function ItemMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [masters, setMasters] = useState({ company: [], quality: [], main_unit: [], alt_unit: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const importRef = useRef(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [params, setParams] = useSearchParams();

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/items/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "item-import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  };

  const importExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/items/import", fd);
      setImportResult(data);
      toast.success(`${data.created} added, ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/items");
      setItems(data);    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadMasters = () =>
    Promise.all(
      ["company", "quality", "main_unit", "alt_unit"].map((t) => api.get(`/masters/${t}`))
    )
      .then(([c, q, mu, au]) =>
        setMasters({
          company: c.data.map((m) => m.name),
          quality: q.data.map((m) => m.name),
          main_unit: mu.data.map((m) => m.name),
          alt_unit: au.data.map((m) => m.name),
        })
      )
      .catch(() => {});

  useEffect(() => {
    load();
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    loadMasters();
  }, []);

  useEffect(() => {
    if (params.get("new") === "1") {
      openForm(null);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openForm = (row, duplicate = false) => {
    setEditItem(duplicate ? null : row);
    setForm(
      row
        ? {
            ...empty,
            ...row,
            product_type: row.product_type || "shade_wise",
            sales_price: row.sales_price ?? "",
            purchase_price: row.purchase_price ?? "",
            min_stock: row.min_stock ?? "",
            gst_pct: row.gst_pct ?? "",
          }
        : empty
    );
    loadMasters();
    setOpen(true);
  };

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    try {
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_path: data.image_path }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  void pickImage;
  void uploading;
  void fileRef;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const base = {
      ...form,
      sales_price: form.sales_price === "" ? null : parseFloat(form.sales_price),
      purchase_price: form.purchase_price === "" ? null : parseFloat(form.purchase_price),
      min_stock: form.min_stock === "" ? 0 : parseFloat(form.min_stock),
      gst_pct: form.gst_pct === "" ? 0 : parseFloat(form.gst_pct),
    };
    delete base.shades_text;
    delete base.shade_number;
    try {
      if (editItem) {
        await api.put(`/items/${editItem.id}`, base);
        toast.success("Item updated");
      } else {
        await api.post("/items", base);
        toast.success("Item added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/items/${deleteRow.id}`);
      toast.success("Item removed");
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band flex flex-col gap-6 rounded-sm px-5 py-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Master data</p>
            <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Item Master</h1>
          </div>
          <div className="flex flex-wrap items-end gap-3 sm:gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Items</p>
              <p className="font-mono-num text-xl font-semibold" data-testid="item-count">
                {items.length}
              </p>
            </div>
            <Button
              data-testid="item-template-button"
              variant="outline"
              className="rounded-sm"
              onClick={downloadTemplate}
            >
              <FileDown size={15} className="mr-2" /> Template
            </Button>
            <Button
              data-testid="item-import-button"
              variant="outline"
              className="rounded-sm border-zinc-950"
              disabled={importing}
              onClick={() => importRef.current?.click()}
            >
              <FileSpreadsheet size={15} className="mr-2" /> {importing ? "Importing…" : "Import Excel"}
            </Button>
            <input
              data-testid="item-import-input"
              ref={importRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => importExcel(e.target.files?.[0])}
            />
            <Button
              data-testid="item-new-button"
              className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              onClick={() => openForm(null)}
            >
              <Plus size={15} className="mr-2" /> New Item
            </Button>
          </div>
        </div>

        {importResult && (
          <div data-testid="item-import-result" className="mt-6 border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold">
                Import finished — {importResult.created} added, {importResult.updated} updated,{" "}
                {importResult.errors.length} skipped
              </p>
              <button
                data-testid="item-import-result-close"
                className="text-zinc-400 hover:text-zinc-950"
                onClick={() => setImportResult(null)}
              >
                <X size={14} />
              </button>
            </div>
            {importResult.errors.length > 0 && (
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-red-600">
                {importResult.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : items.length === 0 ? (
          <div data-testid="item-empty-state" className="mt-8 border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">No items yet</h3>
            <p className="mt-1 text-sm text-zinc-500">
              Add an item with its company, quality, units, HSN code and sales price.
            </p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto border border-zinc-200">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <tr>
                  {["", "Name", "Type", "Print name", "Company", "Quality", "Main unit", "Alt unit", "HSN", "Sales price", "Purchase rate", "Min stock", "GST %", ""].map(
                    (h, i) => (
                      <th key={`${h}-${i}`} className="px-3 py-2 font-bold">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {items.map((row, idx) => (
                  <motion.tr
                    key={row.id}
                    data-testid={`item-row-${row.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: Math.min(idx * 0.02, 0.2) }}
                  >
                    <td className="px-3 py-2">
                      <FabricImage path={row.image_path} alt={row.name} className="h-10 w-10 object-cover" w={200} />
                    </td>
                    <td className="px-3 py-2 font-medium">{row.name}</td>
                    <td className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                      {PRODUCT_TYPE_LABEL[row.product_type || "shade_wise"]}
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{row.print_name || "-"}</td>
                    <td className="px-3 py-2 text-zinc-600">{row.company || "-"}</td>
                    <td className="px-3 py-2 text-zinc-600">{row.quality || "-"}</td>
                    <td className="px-3 py-2 text-zinc-600">{row.main_unit || "-"}</td>
                    <td className="px-3 py-2 text-zinc-600">{row.alternative_unit || "-"}</td>
                    <td className="px-3 py-2 font-mono-num text-zinc-600">{row.hsn_code || "-"}</td>
                    <td className="px-3 py-2 font-mono-num">{row.sales_price ?? "-"}</td>
                    <td className="px-3 py-2 font-mono-num">{row.purchase_price ?? "-"}</td>
                    <td className="px-3 py-2 font-mono-num">{row.min_stock ?? 0}</td>
                    <td className="px-3 py-2 font-mono-num">{row.gst_pct ?? 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`item-edit-${row.id}`}
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 rounded-sm"
                          onClick={() => openForm(row)}
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          data-testid={`item-duplicate-${row.id}`}
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 rounded-sm"
                          title="Duplicate"
                          onClick={() => openForm(row, true)}
                        >
                          <Copy size={13} />
                        </Button>
                        <Button
                          data-testid={`item-delete-${row.id}`}
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 rounded-sm text-zinc-400 hover:text-red-600"
                          onClick={() => setDeleteRow(row)}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="item-form-dialog" className="left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 p-0 sm:max-w-none sm:rounded-none lg:w-[calc(100vw-var(--sb-w))]">
          <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">
              {editItem ? "Edit item" : "New item"}
            </DialogTitle>
            <DialogDescription className="sr-only">Item master details</DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <L>Product type *</L>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    ["shade_wise", "Shade-wise", "item-type-shade-wise"],
                    ["without_shade", "Without shade", "item-type-without-shade"],
                    ["pcs", "PCS", "item-type-pcs"],
                  ].map(([key, label, tid]) => (
                    <Button
                      key={key}
                      type="button"
                      data-testid={tid}
                      variant={form.product_type === key ? "default" : "outline"}
                      size="sm"
                      className={`rounded-sm ${form.product_type === key ? "bg-[var(--mod-primary)] text-white" : ""}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          product_type: key,
                          alternative_unit: key === "pcs" ? "" : f.alternative_unit,
                        }))
                      }
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-zinc-500">
                  {form.product_type === "shade_wise"
                    ? "One product with many shades — every shade keeps its own stock."
                    : form.product_type === "pcs"
                    ? "Sold in pieces. Main unit only, no alternative unit."
                    : "No shades. Piece lengths are typed on the bill, e.g. 10.8*1 12.5*1."}
                </p>
              </div>
              <div>
                <L>Name *</L>
                <Input data-testid="item-name-input" required className="mt-2 rounded-sm" value={form.name} onChange={set("name")} placeholder="e.g. ENGLISH COTTON CS" />
              </div>
              {form.product_type === "shade_wise" && (
                <div className="sm:col-span-2 border border-dashed border-zinc-300 bg-zinc-50 p-3 text-xs text-zinc-600">
                  Shades are not kept here. While billing, type this product name and enter each shade with its
                  meter and than in the shade window.
                </div>
              )}
              <div>
                <L>Print Name</L>
                <Input data-testid="item-print-name-input" className="mt-2 rounded-sm" value={form.print_name ?? ""} onChange={set("print_name")} placeholder="Name printed on bills" />
              </div>
              <div>
                <L>Company</L>
                <MasterSelect
                  testId="item-company-input"
                  options={masters.company}
                  value={form.company}
                  onChange={(v) => setForm((f) => ({ ...f, company: v }))}
                  placeholder="Select company"
                  emptyHint="Add companies in Admin → Company Master"
                />
              </div>
              <div>
                <L>Quality</L>
                <MasterSelect
                  testId="item-quality-input"
                  options={masters.quality}
                  value={form.quality}
                  onChange={(v) => setForm((f) => ({ ...f, quality: v }))}
                  placeholder="Select quality"
                  emptyHint="Add qualities in Admin → Quality Master"
                />
              </div>
              <div>
                <L>Main Unit</L>
                <MasterSelect
                  testId="item-main-unit-input"
                  options={masters.main_unit}
                  value={form.main_unit}
                  onChange={(v) => setForm((f) => ({ ...f, main_unit: v }))}
                  placeholder="Select main unit"
                  emptyHint="Add units in Admin → Main Unit Master"
                />
              </div>
              <div>
                <L>Alternative Unit</L>
                <MasterSelect
                  testId="item-alt-unit-input"
                  options={form.product_type === "pcs" ? [] : masters.alt_unit}
                  value={form.product_type === "pcs" ? "" : form.alternative_unit}
                  onChange={(v) => setForm((f) => ({ ...f, alternative_unit: v }))}
                  placeholder="Select alternative unit"
                  emptyHint={form.product_type === "pcs" ? "Not used for PCS products" : "Add units in Admin → Alternative Unit Master"}
                />
              </div>
              <div>
                <L>HSN Code</L>
                <Input data-testid="item-hsn-input" className="mt-2 rounded-sm" value={form.hsn_code ?? ""} onChange={set("hsn_code")} placeholder="e.g. 5407" />
              </div>
              <div>
                <L>Sales Price</L>
                <Input data-testid="item-price-input" type="number" step="any" className="mt-2 rounded-sm" value={form.sales_price ?? ""} onChange={set("sales_price")} placeholder="0" />
              </div>
              <div>
                <L>Purchase Rate</L>
                <Input data-testid="item-purchase-price-input" type="number" step="any" className="mt-2 rounded-sm" value={form.purchase_price ?? ""} onChange={set("purchase_price")} placeholder="0" />
              </div>
              <div>
                <L>GST %</L>
                <Input data-testid="item-gst-input" type="number" step="any" className="mt-2 rounded-sm" value={form.gst_pct ?? ""} onChange={set("gst_pct")} placeholder="0 / 5 / 12 / 18" />
              </div>
              <div>
                <L>Minimum Stock</L>
                <Input data-testid="item-min-stock-input" type="number" step="any" className="mt-2 rounded-sm" value={form.min_stock ?? ""} onChange={set("min_stock")} placeholder="0" />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="item-save-button"
                data-f2-save
                type="submit"
                disabled={saving}
                className="flex-1 rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              >
                {saving ? "Saving…" : editItem ? "Save changes" : "Add item"}
              </Button>
              <Button
                data-testid="item-cancel-button"
                type="button"
                variant="outline"
                className="rounded-sm px-8"
                onClick={() => setOpen(false)}
              >
                Cancel (Esc)
              </Button>
            </div>
          </form>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head tracking-tight">Remove this item?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow?.name} will be removed from the item master and from inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="item-delete-cancel" className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="item-delete-confirm"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
